import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import api from '../../../../api/client';
import PremiumDatePicker from '../../component/PremiumDatePicker';
import { usePageLoading } from '../../../../contexts/LoadingContext';
import PageHeader, { PAGE_HEADER_CONTROL_CLASS, PageCard, PageHeaderField, PageShell } from '../../../common/PageHeader';

const CHART_TICK = { fontSize: 12, fill: '#525252', fontFamily: "'Poppins', sans-serif" };
const BAR_COLORS = ['#0ea5e9', '#7c3aed', '#10b981', '#f59e0b', '#f43f5e', '#6366f1', '#14b8a6', '#f97316'];

function formatDateInput(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function defaultFromDate() {
  const date = new Date();
  date.setDate(date.getDate() - 89);
  return formatDateInput(date);
}

function formatWhen(iso) {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatNumber(value, suffix) {
  if (value === null || value === undefined) return '—';
  return suffix ? `${value}${suffix}` : String(value);
}

function readStoredUser() {
  try {
    const raw = localStorage.getItem('user') || localStorage.getItem('adminUser');
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    return null;
  }
}

function isCompanyAdminUser(user) {
  return Array.isArray(user?.admin_companies) && user.admin_companies.length > 0;
}

function buildHighlights(data) {
  if (!data) return [];
  const items = [];
  const survey = data.survey;
  const scores = data.scores;
  if (survey?.response_rate != null) {
    items.push(`${survey.response_rate}% of candidates who were offered the questionnaire submitted it.`);
  }
  const rating = survey?.questions?.find((question) => question.question_type === 'rating' && question.average != null);
  if (rating) {
    items.push(`Average experience rating is ${rating.average} out of 5.`);
  }
  const choice = survey?.questions?.find((question) => question.question_type === 'single_choice');
  const topChoice = choice?.distribution?.slice().sort((left, right) => right.count - left.count)[0];
  if (topChoice?.count) {
    items.push(`Most common answer on “${choice.question_text}” is ${topChoice.label} (${topChoice.count}).`);
  }
  const topBand = scores?.distribution?.slice().sort((left, right) => right.count - left.count)[0];
  if (scores?.count && topBand?.count) {
    items.push(`${topBand.count} of ${scores.count} reports fall in the ${topBand.label} effective-score band.`);
  }
  const weakest = scores?.categories?.slice().sort((left, right) => left.average - right.average)[0];
  if (weakest) {
    items.push(`Lowest average skill score is ${weakest.name} at ${weakest.average} out of 10.`);
  }
  return items.slice(0, 4);
}

function errorMessage(err) {
  const data = err?.response?.data;
  if (!data) return 'Could not load feedback insights.';
  if (typeof data.detail === 'string') return data.detail;
  if (Array.isArray(data.detail)) return data.detail.join(' ');
  const firstKey = Object.keys(data)[0];
  const value = data[firstKey];
  if (Array.isArray(value)) return value.join(' ');
  if (typeof value === 'string') return value;
  return 'Could not load feedback insights.';
}

function CountTooltip({ active, payload, valueLabel }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm shadow-md">
      <p className="font-semibold text-gray-900">{row.label}</p>
      <p className="text-gray-600">
        {payload[0].value} {valueLabel}
      </p>
    </div>
  );
}

function CountBarChart({ data, horizontal, color, valueLabel, allowDecimals, colorByBar }) {
  const height = horizontal ? Math.max(160, data.length * 36 + 16) : 240;
  const cells = colorByBar
    ? data.map((item, index) => (
        <Cell key={item.label} fill={BAR_COLORS[index % BAR_COLORS.length]} />
      ))
    : null;
  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        {horizontal ? (
          <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
            <CartesianGrid stroke="rgba(229,229,229,0.7)" horizontal={false} />
            <XAxis type="number" allowDecimals={allowDecimals} tick={CHART_TICK} axisLine={false} />
            <YAxis
              type="category"
              dataKey="label"
              width={120}
              tick={CHART_TICK}
              axisLine={false}
              tickFormatter={(value) => (String(value).length > 18 ? `${String(value).slice(0, 18)}…` : value)}
            />
            <Tooltip content={<CountTooltip valueLabel={valueLabel} />} />
            <Bar dataKey="value" fill={color} radius={[0, 6, 6, 0]} maxBarSize={18}>
              {cells}
            </Bar>
          </BarChart>
        ) : (
          <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="rgba(229,229,229,0.7)" vertical={false} />
            <XAxis dataKey="label" tick={CHART_TICK} axisLine={false} />
            <YAxis allowDecimals={allowDecimals} tick={CHART_TICK} axisLine={false} />
            <Tooltip content={<CountTooltip valueLabel={valueLabel} />} />
            <Bar dataKey="value" fill={color} radius={[6, 6, 0, 0]} maxBarSize={42}>
              {cells}
            </Bar>
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

function KpiCard({ label, value, hint }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-gray-900">{value}</p>
      {hint ? <p className="mt-1 text-xs text-gray-500">{hint}</p> : null}
    </div>
  );
}

function QuestionCard({ question }) {
  const answered = (question.distribution || []).reduce((sum, item) => sum + item.count, 0);
  const isChoice = question.question_type === 'single_choice' || question.question_type === 'multiple_choice';
  const chartData = (question.distribution || []).map((item) => ({
    label: item.label,
    value: item.count,
  }));

  return (
    <div className={`rounded-xl border border-gray-200 bg-white p-4 shadow-sm ${question.question_type === 'text' ? 'md:col-span-2' : ''}`}>
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{question.question_text}</h3>
          {question.removed ? (
            <p className="mt-1 text-xs text-gray-500">This question is no longer on the questionnaire.</p>
          ) : null}
          {question.question_type === 'multiple_choice' ? (
            <p className="mt-1 text-xs text-gray-500">A response can count toward more than one option.</p>
          ) : null}
        </div>
        {question.question_type === 'rating' && question.average != null ? (
          <span className="shrink-0 rounded-full bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-700">
            Avg {question.average} / 5
          </span>
        ) : null}
      </div>
      {question.question_type === 'text' ? (
        <CommentList comments={question.comments} />
      ) : answered === 0 ? (
        <p className="text-sm text-gray-500">No answers in this range.</p>
      ) : (
        <CountBarChart
          data={chartData}
          horizontal={isChoice}
          color="#0ea5e9"
          colorByBar
          valueLabel="responses"
          allowDecimals={false}
        />
      )}
    </div>
  );
}

function ResponseMixPie({ submitted, skipped }) {
  const slices = [
    { label: 'Submitted', value: submitted || 0, color: '#0ea5e9' },
    { label: 'Skipped', value: skipped || 0, color: '#f59e0b' },
  ].filter((slice) => slice.value > 0);
  const total = (submitted || 0) + (skipped || 0);
  if (!total) return null;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-gray-900">Submitted and skipped</h3>
      <p className="mt-1 mb-3 text-xs text-gray-500">Share of candidates who were offered the questionnaire.</p>
      <div style={{ width: '100%', height: 220 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={slices}
              dataKey="value"
              nameKey="label"
              innerRadius={58}
              outerRadius={86}
              paddingAngle={2}
              stroke="#ffffff"
              strokeWidth={2}
            >
              {slices.map((slice) => (
                <Cell key={slice.label} fill={slice.color} />
              ))}
            </Pie>
            <Tooltip content={<CountTooltip valueLabel="responses" />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-1 flex flex-wrap justify-center gap-4 text-xs text-gray-600">
        {slices.map((slice) => (
          <span key={slice.label} className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: slice.color }} />
            {slice.label} · {slice.value} ({Math.round((slice.value / total) * 100)}%)
          </span>
        ))}
      </div>
    </div>
  );
}

function CommentList({ comments }) {
  if (!comments?.length) {
    return <p className="text-sm text-gray-500">No written comments in this range.</p>;
  }
  return (
    <ul className="space-y-3">
      {comments.map((comment, index) => (
        <li key={`${comment.submitted_at || 'comment'}-${index}`} className="rounded-lg bg-gray-50 px-3 py-2">
          <p className="text-sm text-gray-800">{comment.text}</p>
          <p className="mt-1 text-xs text-gray-500">
            {comment.candidate_name || 'Candidate'} · {formatWhen(comment.submitted_at)}
          </p>
        </li>
      ))}
    </ul>
  );
}

export default function FeedbackInsightsPage() {
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [from, setFrom] = useState(defaultFromDate);
  const [to, setTo] = useState(() => formatDateInput(new Date()));
  const today = formatDateInput(new Date());
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [advice, setAdvice] = useState(null);
  const [advising, setAdvising] = useState(false);
  const [adviceError, setAdviceError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function checkAccess() {
      const user = readStoredUser();
      if (!isCompanyAdminUser(user)) {
        if (!cancelled) {
          setAccessDenied(true);
          setCheckingAccess(false);
        }
        return;
      }

      try {
        const response = await api.get('admin/company-admin-check/');
        if (cancelled) return;
        const adminCompanies = response.data?.admin_companies || [];
        if (!response.data?.is_company_admin || adminCompanies.length === 0) {
          setAccessDenied(true);
          return;
        }
        setCompanies(adminCompanies);
        setCompanyId(String(adminCompanies[0].company_id));
      } catch (err) {
        if (!cancelled) {
          setAccessDenied(true);
        }
      } finally {
        if (!cancelled) setCheckingAccess(false);
      }
    }

    checkAccess();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setAdvice(null);
    setAdviceError('');
  }, [companyId, from, to]);

  useEffect(() => {
    if (!companyId || accessDenied || !from || !to || from > to) return undefined;
    let cancelled = false;

    async function loadInsights() {
      setLoading(true);
      setError('');
      try {
        const response = await api.get('feedback-insights/', {
          params: {
            company_id: companyId,
            from,
            to,
            page,
          },
        });
        if (!cancelled) setData(response.data);
      } catch (err) {
        if (!cancelled) {
          setData(null);
          setError(errorMessage(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadInsights();
    return () => {
      cancelled = true;
    };
  }, [companyId, from, to, page, accessDenied]);

  usePageLoading(checkingAccess || (loading && !data), {
    message: 'Loading feedback insights…',
  });

  if (checkingAccess) return null;

  if (accessDenied) {
    return (
      <PageShell fill={false}>
        <PageCard className="text-center">
          <PageHeader title="Feedback Insights" />
          <p className="px-6 py-4 text-sm text-gray-600">
            This page is available to company admins.
          </p>
        </PageCard>
      </PageShell>
    );
  }

  const survey = data?.survey;
  const scores = data?.scores;
  const responses = data?.responses;
  const hasSurveyResponses = (survey?.submitted || 0) + (survey?.skipped || 0) > 0;
  const canAdvise = hasSurveyResponses || (scores?.count || 0) > 0;
  const highlights = buildHighlights(data);

  async function requestAdvice() {
    if (!canAdvise || advising) return;
    setAdvising(true);
    setAdviceError('');
    try {
      const response = await api.post('feedback-insights/advice/', {}, {
        params: { company_id: companyId, from, to },
      });
      setAdvice(response.data);
    } catch (err) {
      setAdvice(null);
      setAdviceError(errorMessage(err));
    } finally {
      setAdvising(false);
    }
  }

  return (
    <PageShell fill={false} className="font-sans">
      <PageCard>
        <PageHeader
          title="Feedback Insights"
          subtitle="Candidate survey responses and AI interview scores for your company."
          actionsClassName="items-end"
        >
          {companies.length > 1 ? (
            <PageHeaderField label="Company">
              <select
                value={companyId}
                onChange={(event) => {
                  setCompanyId(event.target.value);
                  setPage(1);
                }}
                className={PAGE_HEADER_CONTROL_CLASS}
              >
                {companies.map((company) => (
                  <option key={company.company_id} value={company.company_id}>
                    {company.company__name}
                  </option>
                ))}
              </select>
            </PageHeaderField>
          ) : null}
          <PageHeaderField label="From">
            <PremiumDatePicker
              compact
              value={from}
              max={to < today ? to : today}
              onChange={(event) => {
                const next = event.target.value;
                if (next && next > today) return;
                setFrom(next);
                setPage(1);
              }}
            />
          </PageHeaderField>
          <PageHeaderField label="To">
            <PremiumDatePicker
              compact
              value={to}
              min={from}
              max={today}
              onChange={(event) => {
                const next = event.target.value;
                if (next && next > today) return;
                setTo(next);
                setPage(1);
              }}
              align="right"
            />
          </PageHeaderField>
        </PageHeader>

        <div className="space-y-8 p-4 sm:p-6">
          {error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              {error}
            </div>
          ) : null}

          {data ? (
            <>
              <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">What the data says</h2>
                    <p className="mt-1 text-sm text-gray-500">
                      Highlights come from the charts below. AI suggestions stay off until you ask for them.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={requestAdvice}
                    disabled={!canAdvise || advising}
                    className="inline-flex shrink-0 items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                    style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}
                  >
                    <Sparkles className="h-4 w-4" />
                    {advising ? 'Reading the data…' : advice ? 'Refresh suggestions' : 'Get AI suggestions'}
                  </button>
                </div>
                {highlights.length ? (
                  <ul className="mt-4 space-y-2 text-sm text-gray-800">
                    {highlights.map((item) => (
                      <li key={item} className="flex gap-2">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-sky-500" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-4 text-sm text-gray-500">No feedback in this date range yet.</p>
                )}
                {adviceError ? (
                  <p className="mt-4 text-sm text-red-700">{adviceError}</p>
                ) : null}
                {advice ? (
                  <div className="mt-4 space-y-4 rounded-lg bg-slate-50 p-4">
                    <div>
                      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Opinion</h3>
                      <p className="mt-1 text-sm leading-6 text-gray-800">{advice.opinion}</p>
                    </div>
                    <div>
                      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Suggestions</h3>
                      <ol className="mt-2 list-decimal space-y-2 pl-5 text-sm leading-6 text-gray-800">
                        {advice.suggestions.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ol>
                    </div>
                  </div>
                ) : null}
              </section>

              <section className={loading ? 'opacity-60' : ''}>
                <div className="mb-4">
                  <h2 className="text-base font-semibold text-gray-900">Candidate survey</h2>
                  <p className="text-sm text-gray-500">
                    {data.questionnaire?.exists
                      ? data.questionnaire.title
                      : 'Post-interview questionnaire'}
                  </p>
                </div>

                {!data.questionnaire?.exists ? (
                  <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-sm text-gray-600">
                    No feedback questionnaire has been set up yet.{' '}
                    <Link to="/settings/interview-configuration?tab=questionnaire" className="font-semibold text-sky-700 hover:underline">
                      Create one in Settings
                    </Link>
                    .
                  </div>
                ) : null}

                {data.questionnaire?.exists && !data.questionnaire.is_enabled ? (
                  <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    This questionnaire is turned off. Charts still include responses collected while it was on.
                  </div>
                ) : null}

                {data.questionnaire?.exists ? (
                  <>
                    <div className="grid gap-3 sm:grid-cols-3">
                      <KpiCard label="Submitted" value={survey.submitted} />
                      <KpiCard label="Skipped" value={survey.skipped} />
                      <KpiCard
                        label="Response rate"
                        value={formatNumber(survey.response_rate, '%')}
                        hint="Submitted out of submitted and skipped"
                      />
                    </div>
                    {hasSurveyResponses ? (
                      <>
                        <div className="mt-4 grid gap-4 lg:grid-cols-3">
                          {survey.questions
                            .filter((question) => question.question_type !== 'text')
                            .map((question) => (
                              <QuestionCard key={question.id} question={question} />
                            ))}
                          <ResponseMixPie submitted={survey.submitted} skipped={survey.skipped} />
                        </div>
                        {survey.questions.some((question) => question.question_type === 'text') ? (
                          <div className="mt-4 grid gap-4">
                            {survey.questions
                              .filter((question) => question.question_type === 'text')
                              .map((question) => (
                                <QuestionCard key={question.id} question={question} />
                              ))}
                          </div>
                        ) : null}
                      </>
                    ) : (
                      <p className="mt-4 text-sm text-gray-500">No questionnaire responses in this date range.</p>
                    )}
                  </>
                ) : null}
              </section>

              <section className={loading ? 'opacity-60' : ''}>
                <div className="mb-4">
                  <h2 className="text-base font-semibold text-gray-900">AI interview scores</h2>
                  <p className="text-sm text-gray-500">
                    Scores from generated interview reports. Effective score is out of 100. Skill scores are out of 10.
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  <KpiCard label="Reports" value={scores.count} />
                  <KpiCard label="Avg effective score" value={formatNumber(scores.avg_effective)} />
                  <KpiCard label="Avg total score" value={formatNumber(scores.avg_total)} />
                </div>
                {scores.count === 0 ? (
                  <p className="mt-4 text-sm text-gray-500">No interview reports in this date range.</p>
                ) : (
                  <div className="mt-4 grid gap-4 lg:grid-cols-2">
                    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                      <h3 className="mb-3 text-sm font-semibold text-gray-900">Effective score</h3>
                      <CountBarChart
                        data={scores.distribution.map((item) => ({ label: item.label, value: item.count }))}
                        color="#7c3aed"
                        valueLabel="interviews"
                        allowDecimals={false}
                      />
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                      <h3 className="mb-1 text-sm font-semibold text-gray-900">Skill categories</h3>
                      <p className="mb-3 text-xs text-gray-500">Categories that appear most often in this range.</p>
                      {scores.categories.length === 0 ? (
                        <p className="text-sm text-gray-500">No category scores in this range.</p>
                      ) : (
                        <CountBarChart
                          data={scores.categories.map((item) => ({ label: item.name, value: item.average }))}
                          horizontal
                          color="#0ea5e9"
                          colorByBar
                          valueLabel="avg / 10"
                          allowDecimals
                        />
                      )}
                    </div>
                  </div>
                )}
              </section>

              <section className={loading ? 'opacity-60' : ''}>
                <div className="mb-4 flex items-end justify-between gap-3">
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">Responses</h2>
                    <p className="text-sm text-gray-500">Each row is one candidate questionnaire.</p>
                  </div>
                  {responses?.num_pages > 1 ? (
                    <div className="flex items-center gap-2 text-sm">
                      <button
                        type="button"
                        onClick={() => setPage((current) => Math.max(1, current - 1))}
                        disabled={page <= 1}
                        className="rounded-lg border border-gray-200 px-3 py-1.5 font-medium text-gray-700 disabled:opacity-40"
                      >
                        Previous
                      </button>
                      <span className="text-gray-500">
                        {page} / {responses.num_pages}
                      </span>
                      <button
                        type="button"
                        onClick={() => setPage((current) => current + 1)}
                        disabled={page >= responses.num_pages}
                        className="rounded-lg border border-gray-200 px-3 py-1.5 font-medium text-gray-700 disabled:opacity-40"
                      >
                        Next
                      </button>
                    </div>
                  ) : null}
                </div>
                <div className="overflow-x-auto rounded-xl border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-4 py-3 font-semibold">Candidate</th>
                        <th className="px-4 py-3 font-semibold">Job</th>
                        <th className="px-4 py-3 font-semibold">Submitted</th>
                        <th className="px-4 py-3 font-semibold">Status</th>
                        <th className="px-4 py-3 font-semibold">Report</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {responses?.results?.length ? responses.results.map((row) => (
                        <tr key={row.id}>
                          <td className="px-4 py-3 font-medium text-gray-900">
                            {row.candidate_name || 'Unknown candidate'}
                          </td>
                          <td className="px-4 py-3 text-gray-700">{row.job_title || '—'}</td>
                          <td className="px-4 py-3 text-gray-700">{formatWhen(row.submitted_at)}</td>
                          <td className="px-4 py-3">
                            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${row.is_skipped ? 'bg-gray-100 text-gray-600' : 'bg-emerald-50 text-emerald-700'}`}>
                              {row.is_skipped ? 'Skipped' : 'Submitted'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {row.candidate_id && row.session_id ? (
                              <button
                                type="button"
                                onClick={() => {
                                  window.open(
                                    `/feedbacks/${row.company_id}/${row.candidate_id}/${row.session_id}`,
                                    '_blank',
                                    'noopener,noreferrer',
                                  );
                                }}
                                className="font-semibold text-sky-700 hover:underline"
                              >
                                View report
                              </button>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan={5} className="px-4 py-6 text-center text-gray-500">
                            No questionnaire responses in this date range.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          ) : null}
        </div>
      </PageCard>
    </PageShell>
  );
}
